import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import './UserCreate.css';

const StoreCreate = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { company } = location.state || {};

    const [units, setUnits] = useState([]);
    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [loadingUnits, setLoadingUnits] = useState(false);
    const [storeName, setStoreName] = useState('');
    const [storeDisplayName, setStoreDisplayName] = useState('');
    const [appVersionName, setAppVersionName] = useState('default');
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [storeNameError, setStoreNameError] = useState('');

    useEffect(() => {
        if (company?.id) {
            setLoadingUnits(true);
            apiService.getCompanyUnits(company.id).then((result) => {
                if (result.success && result.data?.units) {
                    setUnits(result.data.units);
                }
                setLoadingUnits(false);
            });
        }
    }, [company?.id]);

    // O nome da loja NÃO é rótulo: ele vira caminho no S3 e pedaço do nome do arquivo de áudio
    // (`{store_name}/{ano}/{mes}/{dia}/...` e `loja__mic__label__vendedor__data__rms.flac`).
    // Acento, ç, espaço e caractere especial quebram a chave do S3 e o `parse_filename` que lê o
    // arquivo de volta — e o estrago aparece só depois, na gravação que não é processada.
    // Por isso a trava é no cadastro, que é o único momento em que dá para evitar: o nome NÃO é
    // editável depois (o campo é `disabled` na tela de edição).
    const STORE_NAME_RE = /^[A-Za-z0-9_-]+$/;
    const STORE_NAME_HINT = 'Use apenas letras sem acento (A-Z, a-z), números, _ e -. '
        + 'Sem espaços, sem ç e sem caracteres especiais.';

    const validaStoreName = (value) => {
        if (!value) return '';
        if (/\s/.test(value)) return 'O nome da loja não pode conter espaços em branco.';
        if (!STORE_NAME_RE.test(value)) {
            // Caractere invisível vira PALAVRA: "não permitido:  " não comunica nada a quem lê.
            const NOME_DO_CHAR = { ' ': 'espaço', '\t': 'tabulação' };
            const proibidos = [...new Set(value.split('').filter((c) => !/[A-Za-z0-9_-]/.test(c)))]
                .map((c) => NOME_DO_CHAR[c] || c);
            return `Caractere não permitido: ${proibidos.join(' ')}. ${STORE_NAME_HINT}`;
        }
        return '';
    };

    const handleStoreNameChange = (e) => {
        const value = e.target.value;
        setStoreNameError(validaStoreName(value));
        setStoreName(value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (!selectedUnitId) {
            setMessage({ type: 'error', text: 'Selecione uma unidade.' });
            return;
        }

        if (!storeName.trim() || !storeDisplayName.trim()) {
            setMessage({ type: 'error', text: 'Preencha todos os campos.' });
            return;
        }

        const erroNome = validaStoreName(storeName.trim());
        if (erroNome) {
            setStoreNameError(erroNome);
            setMessage({ type: 'error', text: erroNome });
            return;
        }

        if (!company?.id) {
            setMessage({ type: 'error', text: 'Empresa não identificada. Volte e tente novamente.' });
            return;
        }

        try {
            setSubmitting(true);
            const result = await apiService.createStore({
                company_id: company.id,
                unit_id: Number(selectedUnitId),
                store_name: storeName.trim(),
                store_display_name: storeDisplayName.trim(),
                app_version_name: appVersionName,
            });

            if (result.success) {
                setMessage({ type: 'success', text: `Loja "${storeDisplayName.trim()}" cadastrada com sucesso!` });
                setStoreName('');
                setStoreDisplayName('');
                setSelectedUnitId('');
                setAppVersionName('default');
            } else {
                setMessage({ type: 'error', text: result.message || 'Erro ao cadastrar loja.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Erro ao cadastrar loja. Tente novamente.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="user-create">
            <div className="user-create-header">
                <button className="btn-back" onClick={() => navigate('/users')}>
                    &#8592; Voltar
                </button>
                <h1>Cadastrar Loja</h1>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            <form className="user-create-form" onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="company">Empresa</label>
                        <input
                            id="company"
                            type="text"
                            value={company?.name || ''}
                            disabled
                            className="form-input"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="unit">Unidade</label>
                        <select
                            id="unit"
                            value={selectedUnitId}
                            onChange={(e) => setSelectedUnitId(e.target.value)}
                            className="form-input"
                            disabled={loadingUnits}
                        >
                            <option value="">
                                {loadingUnits ? 'Carregando...' : 'Selecione uma unidade'}
                            </option>
                            {units.map((unit) => (
                                <option key={unit.id} value={unit.id}>
                                    {unit.name}{unit.nickname ? ` (${unit.nickname})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="storeName">Nome da Loja (identificador técnico)</label>
                        <input
                            id="storeName"
                            type="text"
                            value={storeName}
                            onChange={handleStoreNameChange}
                            className={`form-input${storeNameError ? ' form-input-error' : ''}`}
                            placeholder="ex: loja_centro"
                            autoFocus
                        />
                        {storeNameError
                            ? <span className="field-error">{storeNameError}</span>
                            : <small className="field-hint">
                                {STORE_NAME_HINT} Este nome vira pasta no armazenamento e parte do
                                nome do arquivo de áudio, e <strong>não pode ser alterado depois</strong>.
                                Para o nome que aparece na tela, use o campo ao lado.
                              </small>}
                    </div>
                    <div className="form-group">
                        <label htmlFor="storeDisplayName">Nome de Exibição</label>
                        <input
                            id="storeDisplayName"
                            type="text"
                            value={storeDisplayName}
                            onChange={(e) => setStoreDisplayName(e.target.value)}
                            className="form-input"
                            placeholder="ex: Loja Centro"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="appVersionName">Versão do app</label>
                        <select
                            id="appVersionName"
                            value={appVersionName}
                            onChange={(e) => setAppVersionName(e.target.value)}
                            className="form-input"
                        >
                            <option value="default">default</option>
                            <option value="gamification">gamification</option>
                        </select>
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => navigate('/users')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-submit"
                        disabled={submitting || !!storeNameError}
                    >
                        {submitting ? 'Cadastrando...' : 'Cadastrar Loja'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default StoreCreate;
